import canvas from '@classes/canvas'
import { getPropertyValue } from '@constants/utils'
import Logger from '@logger'
import type { Metric } from '@shared_types/vessel_data'
import { isNonEmptyArray } from '@shared_utils/array_utils'
import { degreesToRadians } from '@turf/helpers'
import { isNumber } from 'lodash'

import { divideIfNotZero } from '../helper/canvasDraw'
import type { MainParams, StyleParams } from '../Meter'
import type { GaugeDrawResult } from './gauge_types'

const logger = Logger.create('Gauges/Depth')

export function drawDepthGauge (main: MainParams, style: StyleParams, data: Metric[] | undefined, size: number): GaugeDrawResult {
  const { prevValue, value, ctx, center, dpx, radius, formatter, redraw, unit, currentValue } = main
  const { arcColor, color } = style

  const depthGradientColors = JSON.parse(getPropertyValue('--meter-depth-gradient-colors'))

  let startAngle
  let progressAngle
  let newCurrentValue = currentValue

  const depthData = data && data.length > 15 ? data.slice(data.length - 15) : data || []
  const gap = 1.6
  const barWidth = size / 2 / 15 - (size / 2 / 15) * 0.42
  const barHeight = 15
  const valueData = depthData.map(val => val.value)
  const maxVal = isNonEmptyArray(valueData) ? Math.max(...valueData) : 0

  const from = Number(prevValue)
  const to = Number(value)

  const drawing = (current: number) => {
    if (!!Number(maxVal) && current > Number(maxVal)) {
      newCurrentValue = Number(maxVal)
    } else {
      newCurrentValue = current
    }

    const calcValue = `${!!Number(maxVal) && newCurrentValue > Number(maxVal) ? '>' : ''} ${formatter(newCurrentValue)}`
    const valPos = ctx.measureText(calcValue)
    const angle = 90 * divideIfNotZero(newCurrentValue, Number(maxVal))
    startAngle = degreesToRadians(90 - angle)
    progressAngle = degreesToRadians(90 + angle)

    logger.debug(`depth => startAngle=${startAngle}, progressAngle=${progressAngle}`)

    const val = newCurrentValue / (maxVal || 0)

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    redraw()
    canvas.drawValue(
      ctx,
      center - Number(valPos.width) / calcValue.length - 6 * dpx,
      center - center / 2 - 4 * dpx,
      calcValue,
      14 * dpx,
      color
    )
    canvas.drawArc(ctx, center, center, radius, 0, Math.PI * 2, arcColor, dpx + 1)
    ctx.clip()

    const start = canvas.xy(70, center, radius - dpx)
    const end = canvas.xy(290, center, radius - dpx)
    const diff = ((size - start.y) * val) / 1.65
    const grd = ctx.createLinearGradient(0, 0, size * 1.2, size * 1.3)
    grd.addColorStop(0, depthGradientColors[0])
    grd.addColorStop(0.5, depthGradientColors[1])
    grd.addColorStop(1, depthGradientColors[2])
    let startIndex = 0
    if (depthData.length < 15) {
      startIndex = 15
    }

    depthData?.forEach((data, index) => {
      if (maxVal && value && isNumber(value)) {
        const percent = data.value / (maxVal + (maxVal * (start.y + barHeight)) / size)
        const y = ((size - start.y) * percent) / 1.5

        startIndex--
        depthData.length >= 15 && (startIndex = index)
        ctx.beginPath()
        ctx.fillStyle = grd
        ctx.rect(end.x + startIndex * barWidth * gap, start.y + y, barWidth, barHeight)
        ctx.fill()
        ctx.closePath()
      }
    })

    // x line
    canvas.drawLine(ctx, start.x, start.y, end.x, end.y, arcColor, dpx + 1)

    // y line
    canvas.drawLine(ctx, center, start.y, center, radius * 2, arcColor, 0.4 * dpx)

    // depth circle
    canvas.drawArc(ctx, center, start.y + diff, dpx * 2, 0, Math.PI * 2, 'transparent', 1, arcColor)
    unit
      && canvas.drawUnit(
        ctx,
        center + Number(valPos.width) / 2 + calcValue.length * dpx - 4 * dpx,
        center - center / 2 - 4 * dpx + dpx,
        unit,
        dpx * 9,
        color
      )
    canvas.drawArc(ctx, center, center, radius, 0, Math.PI * 2, arcColor, dpx + 1)
  }

  const animateId = canvas.animate(from, to, drawing, 20, 'depth')

  return { animateId, newCurrentValue }
}
